import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  Box,
  ClickAwayListener,
  Typography,
  Unstable_TrapFocus as FocusTrap,
} from "@mui/material";
import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";
import { Cancel01Icon, Logout05Icon } from "@hugeicons-pro/core-stroke-rounded";
import {
  IconButton,
  SecondaryNav,
  SideNav,
  SideNavFooter,
  SideNavHeader,
  SideNavItem,
  SideNavList,
  SideNavSubItem,
} from "@outpave/ds";
import { useLogoForScheme, useSymbolForScheme } from "utils/themeContextHelper";
import { useSubNav } from "context/SubNavContext";

// ── Public types ─────────────────────────────────────────────────────────────

/** Configuration for a single sidebar nav item. */
export interface NavItemConfig {
  /** HugeIcons icon rendered beside the label. */
  IconComponent?: IconSvgElement;
  /** Display label (passed through i18n). */
  name: string;
  /**
   * Relative URL segment passed to `getRoute`.
   * If `to` is also set, `to` takes precedence and this is used only
   * for active-state matching.
   */
  url: string;
  /** Override which URL segment triggers the active highlight. Defaults to `url`. */
  urlToMatch?: string;
  /**
   * How this item's sub-items appear. Omit for an item with none.
   * - `"dropdown"` — they expand inline underneath the row.
   * - `"drawer"` — they unfold as a column beside the rail.
   */
  subItemDisplayType?: "dropdown" | "drawer";
  /**
   * When exactly one sub-item survives visibility filtering, skip the sub-nav
   * entirely and navigate straight to it. Admin's Expenses does this: with
   * reimbursements gated off, "Card Expenses" is the only child and a
   * one-item dropdown is worse than a direct link.
   */
  collapseWhenSingleSubItem?: boolean;
  subItemsAtRoot?: boolean;
  /** Set to false to hide the item entirely (role-based visibility). */
  isVisible?: boolean;
  /**
   * Absolute path that bypasses `getRoute` entirely.
   * Use for items that navigate outside the current shell (e.g. `/logout`).
   */
  to?: string;
}

/** Configuration for a sub-item shown under its parent. */
export interface SubNavItemConfig {
  /** Display label (passed through i18n). */
  name: string;
  /** Relative URL segment passed to `getSubRoute`. */
  url: string;
  /** Override which URL segment triggers the active highlight. Defaults to `url`. */
  urlToMatch?: string;
  /** Must match the parent `NavItemConfig.url` that owns this sub-item. */
  parentUrl: string;
  /**
   * Optional uppercase heading this item sits under in the flyout. Items
   * sharing one are kept together, in the order the group is first seen;
   * ungrouped items lead, which is the shape `SecondaryNav` takes.
   */
  group?: string;
  /** Set to false to hide the item entirely. */
  isVisible?: boolean;
}

export interface AppSidebarProps {
  /** Toggles the sidebar open/closed — wired up by the parent layout. */
  handleCollapsed: () => void;
  /** Switches the sidebar to full-width mobile layout when true. */
  isSmallScreen: boolean;
  /** Controls whether the mobile overlay is currently visible. */
  isSideBarSmallScreenOpen: boolean;
  /** Primary nav items rendered at the top of the sidebar. */
  items: NavItemConfig[];
  /** Sub-items rendered under their parent when it is expanded. */
  subItems?: SubNavItemConfig[];
  /**
   * Route prefix for this shell, without a trailing slash: `/my`, `/platform`,
   * `/admin`. Every route is built from it, which is the entire difference
   * between the shells' navigation.
   */
  basePath: string;
  /**
   * Destination for the logo link.
   * @default `${basePath}/dashboard/`
   */
  logoHref?: string;
  /**
   * Rail collapsed to an icon strip. Owned by the layout, which offsets its
   * content by the same value.
   * @default false
   */
  collapsed?: boolean;
  /** Omit to pin the rail open: no handler means no toggle. */
  onToggleCollapsed?: () => void;
}

// ── Component ────────────────────────────────────────────────────────────────

/**
 * Shared sidebar shell. Renders the logo, primary nav, optional sub-item
 * flyout, and bottom-pinned items (e.g. Logout).
 *
 * Active state is derived from `location.pathname` — the segment at index 2
 * (`/shell/[segment]/...`) is matched against each item's `urlToMatch`.
 *
 * Only one flyout is open at a time; clicking the same parent again closes it.
 */
const AppSidebar: React.FC<AppSidebarProps> = ({
  handleCollapsed,
  isSmallScreen,
  isSideBarSmallScreenOpen,
  items,
  subItems = [],
  basePath,
  logoHref,
  collapsed = false,
  onToggleCollapsed,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const currentUrl = location.pathname.split("/")[2];
  const subUrl = location.pathname.split("/")[3];
  const { openSubNav: subItemToOpen, showSubNav, toggleSubNav } = useSubNav();

  const getRoute = (url: string) => `${basePath}/${url}/`;

  const parentOf = (sub: SubNavItemConfig) =>
    items.find((item) => item.url === sub.parentUrl);

  const getSubRoute = (sub: SubNavItemConfig) =>
    parentOf(sub)?.subItemsAtRoot
      ? `${basePath}/${sub.url}/`
      : `${basePath}/${sub.parentUrl}/${sub.url}/`;

  const isSubActive = (sub: SubNavItemConfig) =>
    (parentOf(sub)?.subItemsAtRoot ? currentUrl : subUrl) ===
    (sub.urlToMatch ?? sub.url);

  const visibleSubItemsFor = (url: string) =>
    subItems.filter((sub) => sub.parentUrl === url && sub.isVisible !== false);

  /**
   * The single sub-item an item collapses to, or null. Returning the sub-item
   * rather than a boolean is what lets the caller navigate straight to it.
   */
  const collapsedSubFor = (item: NavItemConfig) => {
    if (!item.collapseWhenSingleSubItem) return null;
    const subs = visibleSubItemsFor(item.url);
    return subs.length === 1 ? subs[0] : null;
  };

  /** Sub-items an item actually shows: none when it collapses to a single one. */
  const effectiveSubItemsFor = (item: NavItemConfig) =>
    collapsedSubFor(item) ? [] : visibleSubItemsFor(item.url);

  const handleItemClick = (item: NavItemConfig) => {
    const { url, to } = item;
    const collapsedSub = collapsedSubFor(item);
    const subs = effectiveSubItemsFor(item);
    const opensSubNav = subs.length > 0;

    if (to) {
      navigate(to);
      showSubNav("");
    } else if (collapsedSub) {
      navigate(getSubRoute(collapsedSub));
      showSubNav("");
    } else if (opensSubNav) {
      toggleSubNav(url);
    } else {
      navigate(getRoute(url));
      showSubNav("");
    }

    if (isSmallScreen && !opensSubNav) {
      handleCollapsed();
    }
  };

  const handleSubItemClick = (sub: SubNavItemConfig) => {
    navigate(getSubRoute(sub));
    if (isSmallScreen) handleCollapsed();
    showSubNav("");
  };

  const renderItem = (item: NavItemConfig) => {
    const {
      IconComponent,
      name,
      url,
      urlToMatch = url,
      subItemDisplayType,
      isVisible = true,
    } = item;
    if (!isVisible) return null;

    const subs = effectiveSubItemsFor(item);
    const isDropdown = subs.length > 0 && subItemDisplayType === "dropdown";
    const isDrawer = subs.length > 0 && subItemDisplayType === "drawer";
    const open = subItemToOpen === url;

    return (
      <SideNavItem
        key={name}
        label={t(name)}
        active={
          Boolean(currentUrl?.includes(urlToMatch)) ||
          visibleSubItemsFor(url).some(isSubActive)
        }
        icon={
          IconComponent ? (
            <HugeiconsIcon icon={IconComponent} size={20} strokeWidth={1.5} />
          ) : undefined
        }
        expandable={isDropdown}
        expanded={isDropdown ? open : undefined}
        caret={isDrawer ? "right" : undefined}
        onClick={() => handleItemClick(item)}
      >
        {isDropdown
          ? subs.map((sub) => (
              <SideNavSubItem
                key={sub.name}
                label={t(sub.name)}
                active={isSubActive(sub)}
                onClick={() => handleSubItemClick(sub)}
              />
            ))
          : undefined}
      </SideNavItem>
    );
  };

  useEffect(() => {
    // Only the small-screen drawer reopens from the route: there the panel IS
    // the drawer. On desktop it would open itself on every page load, and a
    // shut drawer would reopen to show it.
    if (!isSmallScreen || !isSideBarSmallScreenOpen) {
      showSubNav("");
      return;
    }
    const parentUrl = location.pathname.split("/")[2];
    const activeItem = items.find((item) => item.url === parentUrl);
    showSubNav(
      activeItem && effectiveSubItemsFor(activeItem).length > 0
        ? parentUrl
        : "",
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSmallScreen, isSideBarSmallScreenOpen]);

  useEffect(() => {
    if (!subItemToOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") showSubNav("");
    };
    window.addEventListener("keydown", onKeyDown);
    // The panel covers the page, so the page must not scroll behind it. On a
    // small screen the drawer holds that lock, and a second one would restore
    // out of order.
    const lockScroll = !isSmallScreen;
    const previousOverflow = document.body.style.overflow;
    if (lockScroll) document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      if (lockScroll) document.body.style.overflow = previousOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subItemToOpen, isSmallScreen]);

  const logo = useLogoForScheme();
  const symbol = useSymbolForScheme();

  const openParent = items.find((item) => item.url === subItemToOpen);

  const flyoutSubItems =
    openParent?.subItemDisplayType === "drawer"
      ? visibleSubItemsFor(subItemToOpen)
      : [];

  const activeSubItem = flyoutSubItems.find(isSubActive);

  const flyoutSections = flyoutSubItems.reduce<
    { heading?: string; items: { id: string; label: string }[] }[]
  >((sections, item) => {
    const heading = item.group ? t(item.group) : undefined;
    const section = sections.find((candidate) => candidate.heading === heading);
    const row = { id: item.urlToMatch ?? item.url, label: t(item.name) };
    if (section) section.items.push(row);
    else sections.push({ heading, items: [row] });
    return sections;
  }, []);

  const flyout = flyoutSubItems.length > 0 && (
    <SecondaryNav
      variant={isSmallScreen ? "inline" : "flyout"}
      sx={{ flex: 1, minHeight: 0 }}
      ariaLabel={t(openParent?.name ?? "")}
      sections={flyoutSections}
      activeId={
        activeSubItem ? (activeSubItem.urlToMatch ?? activeSubItem.url) : ""
      }
      onSelect={(id) => {
        const target = subItems.find(
          (item) => (item.urlToMatch ?? item.url) === id,
        );
        if (!target) return;
        navigate(getSubRoute(target));
        if (isSmallScreen) handleCollapsed();
        showSubNav("");
      }}
    />
  );

  const flyoutPanel = flyout && (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        height: "100%",
        backgroundColor: "var(--color-bg-surface)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "var(--space-3)",
          padding: "var(--space-5) var(--space-4) var(--space-3)",
          flexShrink: 0,
        }}
      >
        <Typography
          variant="h5"
          component="h2"
          sx={{ minWidth: 0, color: "var(--color-text-primary)" }}
        >
          {t(openParent?.name ?? "")}
        </Typography>
        <IconButton
          variant="ghost"
          size="small"
          aria-label={t("Close")}
          onClick={() => showSubNav("")}
          sx={{ flexShrink: 0 }}
        >
          <HugeiconsIcon icon={Cancel01Icon} size={16} />
        </IconButton>
      </Box>
      {flyout}
    </Box>
  );

  const railContents = (
    <>
      <SideNavHeader
        symbol={
          <Box
            component="img"
            src={symbol}
            alt={t("Company Logo")}
            draggable={false}
            sx={{ height: 24, width: "auto" }}
          />
        }
        logo={
          <Link
            to={logoHref ?? `${basePath}/dashboard/`}
            style={{ display: "flex", alignItems: "center", height: "100%" }}
          >
            <Box
              component="img"
              src={logo}
              alt={t("Company Logo")}
              draggable={false}
              sx={{
                height: 28,
                width: "auto",
                maxWidth: 150,
                objectFit: "contain",
              }}
            />
          </Link>
        }
      />

      <SideNavList grow>{items.map(renderItem)}</SideNavList>

      <SideNavFooter>
        <SideNavList>
          <SideNavItem
            label={t("Logout")}
            icon={
              <HugeiconsIcon icon={Logout05Icon} size={20} strokeWidth={1.5} />
            }
            onClick={() => navigate("/logout")}
          />
        </SideNavList>
      </SideNavFooter>
    </>
  );

  if (isSmallScreen) {
    return (
      <SideNav
        variant="temporary"
        open={isSideBarSmallScreenOpen}
        onClose={handleCollapsed}
        // The section panel takes the rail's place and brings its own padding.
        sx={flyoutPanel ? { "& .MuiDrawer-paper": { padding: 0 } } : undefined}
      >
        {flyoutPanel || railContents}
      </SideNav>
    );
  }

  const rail = (
    <SideNav
      sx={{
        height: "100%",
        "& .MuiDrawer-paper": {
          borderRight: "none",
          backgroundColor: "var(--color-bg-surface-canvas-solid)",
        },
      }}
      collapsed={collapsed}
      onToggleCollapsed={onToggleCollapsed}
    >
      {railContents}
    </SideNav>
  );

  return (
    <>
      {flyoutPanel && (
        <Box
          onClick={() => showSubNav("")}
          sx={{
            position: "fixed",
            inset: 0,
            backgroundColor: "var(--color-bg-overlay)",
            zIndex: (theme) => theme.zIndex.drawer - 1,
          }}
        />
      )}

      {/* The trap holds the rail as well as the panel: switching section is how
          you leave, so the rail stays reachable inside it. */}
      <FocusTrap open={Boolean(flyoutPanel)}>
        <Box
          tabIndex={-1}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            height: "100%",
            display: "flex",
            zIndex: (theme) => theme.zIndex.drawer,
            outline: "none",
          }}
        >
          <ClickAwayListener
            mouseEvent={flyoutPanel ? "onClick" : false}
            touchEvent={flyoutPanel ? "onTouchEnd" : false}
            onClickAway={() => showSubNav("")}
          >
            <Box sx={{ display: "flex", height: "100%" }}>
              {rail}
              {flyoutPanel}
            </Box>
          </ClickAwayListener>
        </Box>
      </FocusTrap>
    </>
  );
};

export default AppSidebar;
